<template>
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{$t("intranet.title.page.wineOfTheMonth.list")}}</span>
      <v-spacer></v-spacer>
      <v-btn depressed color="primary" to="/authenticated/wines-of-the-month/create">
        {{$t("intranet.homeContent.button.createWineOfTheMonth")}}
      </v-btn>
    </div>

    <div class="searchable-list-container">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      >
        <template v-slot:item.month="{ item }">{{ formatMonth(item.month) }}</template>
        <template v-slot:item.wine="{ item }">{{ describeWine(item) }}</template>
        <template v-slot:item.published="{ item }">
          <v-chip small :color="item.published ? 'success' : 'grey lighten-1'">
            {{ item.published ? $t('intranet.homeContent.published') : $t('intranet.homeContent.draft') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon :to="`/authenticated/wines-of-the-month/update/${item.id}`">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" @click="askDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card>
        <v-card-title>{{$t('intranet.homeContent.wineOfTheMonth.deleteTitle')}}</v-card-title>
        <v-card-text>{{$t('intranet.homeContent.wineOfTheMonth.deleteConfirmation')}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn color="error" text @click="confirmDelete()">{{$t('intranet.referential.button.delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import WineOfTheMonth from '@/store/models/wine-of-the-month'
import { errorHandler } from 'aveine-toolbox'

export default {
  name: 'Index',
  data () {
    return {
      items: [],
      total: 0,
      loading: false,
      deleteDialog: false,
      itemToDelete: null,
      options: { page: 1, itemsPerPage: 25 },
      APIbaseURL: process.env.VUE_APP_API_HOST
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('intranet.homeContent.wineOfTheMonth.month'), value: 'month', sortable: false, width: 160 },
        { text: this.$t('intranet.homeContent.wineOfTheMonth.wine'), value: 'wine', sortable: false },
        { text: this.$t('intranet.homeContent.attributes.published'), value: 'published', sortable: false, width: 140 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 120 }
      ]
    }
  },
  watch: {
    options: { handler () { this.fetch() }, deep: true }
  },
  methods: {
    /**
     * « septembre 2026 », dans la langue affichee.
     *
     * On lit le prefixe AAAA-MM de la chaine au lieu de la passer a `Date` :
     * l'API serialise avec un `Z` final, et `new Date("2026-09-01T00:00:00Z")`
     * affiche « aout 2026 » pour tout lecteur a fuseau negatif. C'est
     * exactement le glissement que la migration evite en choisissant
     * `timestamp` plutot que `timestamptz` — autant ne pas le reintroduire ici.
     */
    formatMonth (month) {
      if (!month) return '—'
      const [year, monthNumber] = month.slice(0, 7).split('-')
      // Le 1er a midi, en heure LOCALE : aucun fuseau ne peut faire basculer
      // la date d'un jour a l'autre.
      const date = new Date(Number(year), Number(monthNumber) - 1, 1, 12)
      return date.toLocaleDateString(this.$i18n.locale, { month: 'long', year: 'numeric' })
    },

    /** Un vin n'a pas de nom : domaine + cuvee viennent de son template. */
    describeWine (item) {
      const wine = item.wine || {}
      const template = wine.template || {}
      const domain = template.domain || {}
      return [domain.name, template.cuvee, wine.vintage].filter(Boolean).join(' ') || '—'
    },

    async fetch () {
      this.loading = true
      try {
        const response = await WineOfTheMonth.api().get(
          `wines-of-the-month?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            params: {
              include: 'wine.template.domain',
              // Le plus recent d'abord : c'est le mois courant qu'on vient
              // verifier, pas les archives.
              sort: '-month',
              'page[number]': this.options.page,
              'page[size]': this.options.itemsPerPage
            }
          }
        )

        // On passe par vuex-orm pour `withAllRecursive` — sans lui, `wine`
        // resterait un identifiant et la colonne serait vide — mais en
        // RESTREIGNANT aux identifiants de la page qu'on vient de recevoir.
        // Une requete non filtree rendrait tout ce que le store a jamais mis en
        // cache : les lignes s'accumuleraient de page en page.
        const ids = response.entities
          ? (response.entities.wines_of_the_month || []).map(item => item.id)
          : []
        const byId = new Map(
          WineOfTheMonth.query().whereIdIn(ids).withAllRecursive().get().map(i => [i.id, i])
        )
        // L'ordre vient du serveur, pas du store.
        this.items = ids.map(id => byId.get(id)).filter(Boolean)

        // Le total vient du serveur : `items.length` plafonnerait a la taille
        // d'une page et rendrait la page suivante inatteignable.
        this.total = response.response.data.meta
          ? response.response.data.meta.total
          : this.items.length
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    askDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      try {
        // Les etiquettes suivent : `wine_of_the_month_tags` est ON DELETE
        // CASCADE. La suppression est logique, elle libere donc le mois.
        await WineOfTheMonth.api().delete(
          `wines-of-the-month/${this.itemToDelete.id}`,
          { baseURL: this.APIbaseURL, save: false }
        )
        WineOfTheMonth.delete(this.itemToDelete.id)
        this.deleteDialog = false
        this.itemToDelete = null
        await this.fetch()
      } catch (errors) {
        this.deleteDialog = false
        errorHandler(errors, this)
      }
    }
  }
}
</script>
